import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TwoFactorService } from '../../../../core/services/two-factor.service';
import { safeInternalReturnUrl } from '../../../../core/utils/return-url.util';
import * as QRCode from 'qrcode';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-setup-2fa',
    templateUrl: './setup-2fa.component.html',
    styleUrls: ['./setup-2fa.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class Setup2FAComponent implements OnInit {

  // Steps: 0=intro, 1=QR, 2=verify, 3=backup codes
  step = 0;

  loading = false;
  errorMessage = '';

  // Step 1: QR
  qrCodeUrl = '';
  qrImageDataUrl = '';
  manualKey = '';

  // Step 2: Verify
  verifyCode = '';

  // Step 3: Backup codes
  backupCodes: string[] = [];

  /** #1315 — destination d'origine, posée par le garde qui a dérouté ici. */
  private readonly returnUrl: string;

  constructor(
    private twoFactorService: TwoFactorService,
    private router: Router,
    route: ActivatedRoute,
  ) {
    this.returnUrl = safeInternalReturnUrl(
      route.snapshot.queryParams['returnUrl'],
      '/backoffice',
    );
  }

  ngOnInit(): void {
    // Don't auto-start — wait for user to click "J'ai installé l'app"
  }

  goToQrStep(): void {
    this.step = 1;
    this.startSetup();
  }

  async startSetup(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const result = await this.twoFactorService.setup();
      this.qrCodeUrl = result.qrCodeUrl;
      this.manualKey = result.manualEntryKey;

      // Generate QR code image client-side
      if (this.qrCodeUrl) {
        this.qrImageDataUrl = await QRCode.toDataURL(this.qrCodeUrl, {
          width: 250,
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' },
        });
      }
    } catch (err: any) {
      console.error('[Setup2FA] Error:', err);
      this.errorMessage = 'Erreur lors de la configuration. Veuillez réessayer.';
    } finally {
      this.loading = false;
    }
  }

  goToVerify(): void {
    this.step = 2;
    this.errorMessage = '';
    this.verifyCode = '';
  }

  async onVerifyAndEnable(): Promise<void> {
    const trimmed = this.verifyCode.trim();
    if (!trimmed) return;

    this.loading = true;
    this.errorMessage = '';

    try {
      const result = await this.twoFactorService.enable(trimmed);

      if (result.enabled) {
        this.backupCodes = result.backupCodes;
        this.step = 3;
      } else {
        this.errorMessage = 'Code invalide. Réessayez.';
      }
    } catch (err: any) {
      console.error('[Setup2FA] Enable error:', err);
      this.errorMessage = err?.message || 'Code invalide. Vérifiez et réessayez.';
    } finally {
      this.loading = false;
    }
  }

  downloadBackupCodes(): void {
    const content = [
      'Befret Backoffice - Codes de secours 2FA',
      '==========================================',
      '',
      'Conservez ces codes en lieu sûr.',
      'Chaque code ne peut être utilisé qu\'une seule fois.',
      '',
      ...this.backupCodes.map((c, i) => `${i + 1}. ${c}`),
      '',
      `Généré le : ${new Date().toLocaleDateString('fr-BE')}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'befret-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  copyManualKey(): void {
    navigator.clipboard.writeText(this.manualKey).catch(() => {});
  }

  finishSetup(): void {
    this.twoFactorService.mark2FAVerified();
    this.router.navigateByUrl(this.returnUrl);
  }
}
